import { ErrorMessage } from '@hookform/error-message';
import { type FC, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Plus, Trash } from 'react-bootstrap-icons';
import Select from 'react-dropdown-select';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import type { Option } from 'react-multi-select-component';
import { useAuth, useMutationRequest } from '../../../modules/hooks';
import type { DynamicQuestionPayload } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { buildRequestUrl } from '../../../modules/utils/buildRequestUrl';

export interface ModalComponentData {
  show: boolean;
  handleClose: () => void;
  updateTable: () => void;
  isEdit?: boolean;
  defaultQuestion?: DynamicQuestionPayload;
  propertyId: string;
  reservationId?: string;
}

export interface SelectType {
  label: string;
  value: string;
  message?: string;
}

const questionTypes = [
  {
    value: 'text',
    label: 'Text',
  },
  {
    value: 'select',
    label: 'Select',
  },
  {
    value: 'date',
    label: 'Date',
  },
  {
    value: 'time',
    label: 'Time',
  },
];

export const AddQuestionManagerModal: FC<ModalComponentData> = ({
  show,
  handleClose,
  updateTable,
  isEdit = false,
  defaultQuestion,
  propertyId,
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState<Option[]>([]);
  const { credentialsInfo } = useAuth()!;
  const {
    control,
    register,
    handleSubmit,
    reset,
    trigger,

    setValue,
    watch,

    formState: { errors },
  } = useForm<DynamicQuestionPayload>({
    mode: 'onChange',
    defaultValues: {
      question: '',
      type: 'text',
      isRequired: false,
      isActive: true,
      optionsValues: [{ value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'optionsValues',
  });

  const createQuestions = buildRequestUrl(endpoints.DYNAMIC_QUESTIONS, {
    propertyId,
  });

  const [
    {
      data: dynamicQuestionResult,
      loading: loadingDynamicQuestion,
      error: errorDynamicQuestion,
    },
    createDynamicQuestions,
  ] = useMutationRequest<string>(
    createQuestions,
    'post',
    {
      authToken: credentialsInfo?.token,
    },
    { manual: true },
  );

  const updateQuestions = buildRequestUrl(endpoints.UPDATE_DYNAMIC_QUESTIONS, {
    propertyId,
    questionId: defaultQuestion?.uuid || '',
  });

  const [
    {
      data: dynamicQuestionUpdateResult,
      loading: loadingUpdateDynamicQuestion,
      error: errorUpdateDynamicQuestion,
    },
    updateDynamicQuestions,
  ] = useMutationRequest<string>(
    updateQuestions,
    'put',
    {
      authToken: credentialsInfo?.token,
    },
    { manual: true },
  );

  const isRequired = watch('isRequired');
  const isActive = watch('isActive');
  const type = watch('type');

  const AddRequestHandler = async (data: DynamicQuestionPayload) => {
    const { question, type, isActive, isRequired, optionsValues } = data;

    const selectOptions = optionsValues?.map((selectedValue) => selectedValue.value);

    const templateValues: DynamicQuestionPayload = {
      question,
      type,
      selectableValues: selectOptions,
      isActive,
      isRequired,
    };

    if (isEdit) {
      updateDynamicQuestions(templateValues);
    } else {
      createDynamicQuestions(templateValues);
    }
  };

  const onSubmit = async (data: DynamicQuestionPayload) => {
    const validated = await trigger(['question', 'type']);

    if (validated) {
      AddRequestHandler(data);
    }
  };

  const onClose = () => {
    handleClose();
    reset({
      question: '',
      type: 'text',
      isRequired: false,
      isActive: false,
      optionsValues: [{ value: '' }],
    });
    setSelectedQuestion([{ value: 'text', label: 'Text' }]);
  };

  const onIsRequired = (editorState: boolean) => {
    setValue('isRequired', editorState);
  };
  const onIsActive = (editorState: boolean) => {
    setValue('isActive', editorState);
  };

  useEffect(() => {
    if (isEdit && defaultQuestion && show) {
      const { question, type, isRequired, isActive, selectableValues } = defaultQuestion;

      reset({
        question,
        type,
        isRequired,
        isActive,
        optionsValues: selectableValues?.map((opt) => ({ value: opt })) || [
          { value: '' },
        ],
      });

      const templates = [
        {
          value: type,
          label: type[0].toUpperCase() + type.slice(1).toLowerCase(),
        },
      ];

      setSelectedQuestion(templates);
    }
  }, [isEdit, defaultQuestion, show]);

  useEffect(() => {
    if (dynamicQuestionResult || dynamicQuestionUpdateResult) {
      updateTable();
      onClose();
    }
  }, [dynamicQuestionResult, dynamicQuestionUpdateResult]);

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static" keyboard={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title className="editor-title">Create Guest Questions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mt-24px">
              <Col sm={12}>
                <Form.Label>Question</Form.Label>
                <Form.Control
                  {...register('question', { required: 'This is required' })}
                />
                <ErrorMessage
                  errors={errors}
                  name="question"
                  render={({ message }) => <span className="error-text">{message}</span>}
                />
              </Col>
            </Row>

            <Row className="mt-24px">
              <Form.Group className="border-bottom">
                <Form.Label className="editor-title-label">Question Type</Form.Label>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'This is required.' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={questionTypes}
                      values={selectedQuestion}
                      onChange={(values) => {
                        const val = values[0]?.value || '';
                        field.onChange(val);
                        setSelectedQuestion(values);

                        if (val !== 'select') {
                          setValue('optionsValues', [{ value: '' }]);
                        }
                      }}
                    />
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="type"
                  render={({ message }) => <span className="error-text">{message}</span>}
                />
              </Form.Group>
            </Row>

            <Row className="mt-24px">
              <Col sm={6}>
                <Form.Check
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => onIsRequired(e.currentTarget.checked)}
                  label="Is Required"
                />
              </Col>
              <Col sm={6}>
                <Form.Check
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => onIsActive(e.currentTarget.checked)}
                  label="Is Active"
                />
              </Col>
            </Row>
            {type === 'select' && (
              <div className="mt-24px">
                <Form.Label className="mb-16px">Options</Form.Label>
                {fields.map((field, index) => {
                  const isLast = index === fields.length - 1;
                  const currentValue = watch(`optionsValues.${index}.value`);

                  return (
                    <Row key={field.id} className="mb-2">
                      <Col xs={10}>
                        <Form.Control
                          {...register(`optionsValues.${index}.value` as const, {
                            required: 'Option cannot be empty',
                          })}
                          placeholder="Option"
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`optionsValues.${index}.value`}
                          render={({ message }) => (
                            <span className="error-text">{message}</span>
                          )}
                        />
                      </Col>
                      <Col
                        xs={2}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        {isLast ? (
                          <Button
                            variant="success"
                            type="button"
                            onClick={() => append({ value: '' })}
                            disabled={!currentValue}
                          >
                            <Plus />
                          </Button>
                        ) : (
                          <Button
                            variant="danger"
                            type="button"
                            onClick={() => remove(index)}
                          >
                            <Trash />
                          </Button>
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="mt-48px">
          <Button className="cancel-button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="save-button"
            type="submit"
            disabled={loadingDynamicQuestion || loadingUpdateDynamicQuestion}
          >
            {loadingDynamicQuestion || loadingUpdateDynamicQuestion
              ? 'Loading...'
              : 'Save'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
